// src/components/blog/layouts/PostGrid.tsx

'use client'

import { motion } from 'motion/react'
import type { BlogPost } from '~/lib/blog/types'
import { BlogImage } from '~/components/blog/components/BlogImage'
import { BlogAuthor } from '~/components/blog/components/BlogAuthor'
import { BlogMeta } from '~/components/blog/components/BlogMeta'
import Link from 'next/link'
import { cn } from '~/lib/utils'
import { getBlogListingImageUrl } from '~/lib/blog/utils/listingImage'

interface PostGridProps {
  posts: BlogPost[]
  columns?: 1 | 2 | 3 | 4
  showMeta?: boolean
  showAuthor?: boolean
  className?: string
}

export function PostGrid({
  posts,
  columns = 3,
  showMeta = true,
  showAuthor = true,
  className
}: PostGridProps): JSX.Element {
  return (
    <div className={cn(
      "grid gap-6",
      {
        'sm:grid-cols-2': columns >= 2,
        'lg:grid-cols-3': columns >= 3,
        'xl:grid-cols-4': columns === 4
      },
      className
    )}>
      {posts.map((post, index) => (
        <motion.article
          key={post.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          className="group flex flex-col overflow-hidden rounded-2xl bg-white shadow-sm hover:shadow-md dark:bg-gray-800 transition-all duration-200"
        >
          <Link
            href={post.href}
            className="relative aspect-[16/9] overflow-hidden"
          >
            <BlogImage
              src={getBlogListingImageUrl(post)}
              alt={post.title}
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
          </Link>
          <div className="flex flex-1 flex-col p-6">
            {showMeta && (
              <BlogMeta
                post={post}
                className="text-sm"
                showReadingTime={false}
              />
            )}
            <Link href={post.href} className="group mt-3 flex-1">
              <h3 className="text-xl font-semibold tracking-tight text-gray-900 group-hover:text-blue-600 dark:text-white dark:group-hover:text-blue-400">
                {post.title}
              </h3>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 line-clamp-4">
                {post.description}
              </p>
            </Link>
            {showAuthor && (
              <div className="mt-6 border-t border-gray-100 pt-6 dark:border-gray-700">
                <BlogAuthor
                  author={post.author}
                  size="sm"
                  showRole={false}
                />
              </div>
            )}
          </div>
        </motion.article>
      ))}
    </div>
  )
} 